import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, InputNumber, DatePicker, Select, Button, message, Typography, Divider, Space, Alert } from 'antd';
import { DollarSign, Calendar, User, Award, CheckCircle } from 'lucide-react';
import { getPassengers, registerStay } from '../services/userService';
import { getSettings } from '../services/settingService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Passenger {
  id: number;
  name: string;
  last_name: string;
  email: string;
  category: 'classic' | 'premium' | 'silver';
  points?: {
    total_points: number;
  };
}

const RegisterStayPage: React.FC = () => {
  const [form] = Form.useForm();
  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState<Passenger | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [calculatedPoints, setCalculatedPoints] = useState<number>(0);
  const [pointsPerAmount, setPointsPerAmount] = useState<number>(10);
  const [amountForPoints, setAmountForPoints] = useState<number>(50000);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const fetchPassengers = async () => {
    try {
      const response = await getPassengers(1, 1000, ''); // Fetch all passengers
      setPassengers(response.data || []);
    } catch (error) {
      message.error('Error al cargar los huéspedes');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      const settings = response.data || response;

      const pointsPerAmountSetting = settings.find((s: any) => s.key === 'points_per_amount');
      const amountForPointsSetting = settings.find((s: any) => s.key === 'amount_for_points');

      if (pointsPerAmountSetting) setPointsPerAmount(Number(pointsPerAmountSetting.value));
      if (amountForPointsSetting) setAmountForPoints(Number(amountForPointsSetting.value));
    } catch (error) {
      console.error('Error loading settings:', error);
      message.warning('Usando configuración por defecto de puntos');
    }
  };

  const calculatePoints = useCallback(() => {
    if (amount > 0 && amountForPoints > 0) {
      const points = Math.floor((amount / amountForPoints) * pointsPerAmount);
      setCalculatedPoints(points);
    } else {
      setCalculatedPoints(0);
    }
  }, [amount, amountForPoints, pointsPerAmount]);

  useEffect(() => {
    fetchPassengers();
    fetchSettings();
  }, []);

  useEffect(() => {
    calculatePoints();
  }, [amount, pointsPerAmount, amountForPoints, calculatePoints]);

  const handlePassengerChange = (passengerId: number) => {
    const passenger = passengers.find(p => p.id === passengerId);
    setSelectedPassenger(passenger || null);
  };

  const handleSubmit = async (values: any) => {
    if (!selectedPassenger) {
      message.error('Por favor selecciona un huésped');
      return;
    }

    setLoading(true);
    setSuccessMessage('');

    try {
      const description = values.description || 'Estadía registrada desde backoffice';
      const amount = values.amount;

      await registerStay(selectedPassenger.id, amount, description);

      setSuccessMessage(
        `¡Estadía registrada exitosamente! Se asignaron ${calculatedPoints} puntos a ${selectedPassenger.name} ${selectedPassenger.last_name}`
      );

      message.success('Estadía registrada y puntos asignados correctamente');

      // Reset form
      form.resetFields();
      setSelectedPassenger(null);
      setAmount(0);
      setCalculatedPoints(0);

      // Refresh passengers to update points
      fetchPassengers();
    } catch (error: any) {
      message.error(error?.message || 'Error al registrar la estadía');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'premium':
        return '#FFD700';
      case 'silver':
        return '#C0C0C0';
      default:
        return '#CD7F32';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'premium':
        return 'Premium';
      case 'silver':
        return 'Silver';
      default:
        return 'Classic';
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <Calendar size={28} style={{ marginRight: '12px', verticalAlign: 'middle' }} />
          Registrar Estadía
        </Title>
        <Text type="secondary">
          Registra una estadía de un huésped y asigna puntos automáticamente según el monto consumido
        </Text>
      </div>

      {successMessage && (
        <Alert
          message="¡Éxito!"
          description={successMessage}
          type="success"
          showIcon
          icon={<CheckCircle size={20} />}
          closable
          onClose={() => setSuccessMessage('')}
          style={{ marginBottom: '24px' }}
        />
      )}

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            stay_date: dayjs(),
          }}
        >
          {/* Selección de Pasajero */}
          <Form.Item
            label={
              <span>
                <User size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Seleccionar Huésped
              </span>
            }
            name="passenger_id"
            rules={[{ required: true, message: 'Por favor selecciona un huésped' }]}
          >
            <Select
              showSearch
              placeholder="Buscar y seleccionar huésped"
              optionFilterProp="children"
              onChange={handlePassengerChange}
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
              size="large"
            >
              {passengers.map((passenger) => (
                <Option key={passenger.id} value={passenger.id}>
                  {passenger.name} {passenger.last_name} - {passenger.email} ({getCategoryLabel(passenger.category)})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Información del pasajero seleccionado */}
          {selectedPassenger && (
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>Nombre: </Text>
                  <Text>{selectedPassenger.name} {selectedPassenger.last_name}</Text>
                </div>
                <div>
                  <Text strong>Email: </Text>
                  <Text>{selectedPassenger.email}</Text>
                </div>
                <div>
                  <Text strong>Categoría: </Text>
                  <span
                    style={{
                      backgroundColor: getCategoryColor(selectedPassenger.category),
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }}
                  >
                    {getCategoryLabel(selectedPassenger.category)}
                  </span>
                </div>
                <div>
                  <Text strong>Puntos actuales: </Text>
                  <Text style={{ color: '#1890ff', fontSize: '16px', fontWeight: 'bold' }}>
                    {selectedPassenger.points?.total_points || 0} pts
                  </Text>
                </div>
              </Space>
            </div>
          )}

          <Divider />

          {/* Monto Consumido */}
          <Form.Item
            label={
              <span>
                <DollarSign size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Monto Consumido ($)
              </span>
            }
            name="amount"
            rules={[
              { required: true, message: 'Por favor ingresa el monto' },
              { type: 'number', min: 0, message: 'El monto debe ser mayor a 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              placeholder="Ingresa el monto total consumido"
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              onChange={(value) => setAmount(Number(value) || 0)}
            />
          </Form.Item>

          {/* Fecha de Estadía */}
          <Form.Item
            label={
              <span>
                <Calendar size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Fecha de Estadía
              </span>
            }
            name="stay_date"
            rules={[{ required: true, message: 'Por favor selecciona la fecha' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              size="large"
              format="DD/MM/YYYY"
              placeholder="Selecciona la fecha"
            />
          </Form.Item>

          {/* Descripción Opcional */}
          <Form.Item
            label="Descripción (opcional)"
            name="description"
          >
            <Select
              size="large"
              placeholder="Selecciona tipo de estadía"
            >
              <Option value="Habitación Standard">Habitación Standard</Option>
              <Option value="Habitación Deluxe">Habitación Deluxe</Option>
              <Option value="Suite">Suite</Option>
              <Option value="Suite Premium">Suite Premium</Option>
              <Option value="Estadía con servicios adicionales">Estadía con servicios adicionales</Option>
            </Select>
          </Form.Item>

          <Divider />

          {/* Cálculo de Puntos */}
          <div style={{
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
          }}>
            <div style={{ marginBottom: '12px' }}>
              <Award size={20} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#1890ff' }} />
              <Text strong style={{ fontSize: '16px' }}>Cálculo de Puntos</Text>
            </div>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Fórmula: </Text>
                <Text code>
                  (Monto / ${amountForPoints.toLocaleString()}) × {pointsPerAmount} puntos
                </Text>
              </div>
              <div style={{ marginTop: '8px' }}>
                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                  Puntos a asignar: {calculatedPoints} pts
                </Text>
              </div>
              {selectedPassenger && (
                <div>
                  <Text type="secondary">
                    Puntos después de la estadía: {(selectedPassenger.points?.total_points || 0) + calculatedPoints} pts
                  </Text>
                </div>
              )}
            </Space>
          </div>

          {/* Botón de Envío */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              disabled={!selectedPassenger || amount <= 0}
              block
              style={{
                height: '48px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              <CheckCircle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Registrar Estadía y Asignar {calculatedPoints} Puntos
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default RegisterStayPage;
